import type { MapControl } from "../types";

/** Inspect interaction mode (mirrors the state-layer InspectMode). */
export type InspectControlMode = "off" | "popup" | "panel";

export interface InspectControlOptions {
    /** Initial mode. */
    initial?: InspectControlMode;
    /** Fired when the user picks a mode from the flyout. */
    onChange: (mode: InspectControlMode) => void;
}

const MODES: { id: InspectControlMode; label: string }[] = [
    { id: "off", label: "Off" },
    { id: "popup", label: "Popup" },
    { id: "panel", label: "Panel" },
];

const INSPECT_ICON =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>';

/**
 * On-map inspect control: a single square button (matching native controls)
 * that reveals a small flyout of mode options on hover/focus — so switching
 * Off / Popup / Panel is one click without a wide always-on dropdown. The
 * active mode tints the button. Renderer-agnostic {@link MapControl}.
 */
export class InspectControl implements MapControl {
    private container: HTMLDivElement | null = null;
    private button: HTMLButtonElement | null = null;
    private flyout: HTMLDivElement | null = null;
    private mode: InspectControlMode;
    private readonly onChange: (mode: InspectControlMode) => void;

    constructor(options: InspectControlOptions) {
        this.mode = options.initial ?? "off";
        this.onChange = options.onChange;
    }

    onAdd(): HTMLElement {
        const container = document.createElement("div");
        container.className = "maplibregl-ctrl maplibregl-ctrl-group";
        container.style.position = "relative";

        const button = document.createElement("button");
        button.type = "button";
        button.title = "Inspect features";
        button.setAttribute("aria-label", "Inspect features");
        button.innerHTML = INSPECT_ICON;

        const flyout = document.createElement("div");
        this.styleFlyout(flyout);
        MODES.forEach((m) => flyout.appendChild(this.makeOption(m.id, m.label)));

        // Click-to-toggle (NOT hover): a hover menu with a gap between button
        // and flyout vanished as soon as the cursor crossed the gap. Clicking
        // the button opens/closes the flyout; clicking an option or outside
        // closes it.
        button.addEventListener("click", (e) => {
            e.stopPropagation();
            this.setFlyoutOpen(flyout.style.display === "none");
        });

        container.appendChild(button);
        container.appendChild(flyout);
        this.container = container;
        this.button = button;
        this.flyout = flyout;

        // Close on any outside click.
        this.outsideClick = (e: MouseEvent) => {
            if (this.container && !this.container.contains(e.target as Node)) {
                this.setFlyoutOpen(false);
            }
        };
        document.addEventListener("click", this.outsideClick);

        this.applyActiveStyle();
        return container;
    }

    onRemove(): void {
        if (this.outsideClick) {
            document.removeEventListener("click", this.outsideClick);
            this.outsideClick = null;
        }
        this.container?.parentNode?.removeChild(this.container);
        this.container = null;
        this.button = null;
        this.flyout = null;
    }

    private outsideClick: ((e: MouseEvent) => void) | null = null;

    private setFlyoutOpen(open: boolean): void {
        if (this.flyout) this.flyout.style.display = open ? "flex" : "none";
    }

    /** Programmatically set the mode (e.g. from store sync). */
    setMode(mode: InspectControlMode): void {
        this.mode = mode;
        this.applyActiveStyle();
        this.refreshOptionState();
    }

    private makeOption(id: InspectControlMode, label: string): HTMLButtonElement {
        const opt = document.createElement("button");
        opt.type = "button";
        opt.textContent = label;
        opt.dataset.mode = id;
        opt.style.cssText =
            "display:block;width:100%;border:0;background:none;padding:4px 10px;" +
            "text-align:left;cursor:pointer;font:inherit;white-space:nowrap;";
        opt.addEventListener("click", (e) => {
            e.stopPropagation();
            this.mode = id;
            this.applyActiveStyle();
            this.refreshOptionState();
            this.setFlyoutOpen(false);
            this.onChange(id);
        });
        return opt;
    }

    private refreshOptionState(): void {
        this.flyout?.querySelectorAll<HTMLButtonElement>("button[data-mode]").forEach((opt) => {
            const isActive = opt.dataset.mode === this.mode;
            opt.style.fontWeight = isActive ? "700" : "400";
            opt.style.backgroundColor = isActive ? "#f0f4f8" : "";
        });
    }

    private styleFlyout(flyout: HTMLDivElement): void {
        flyout.style.cssText =
            "display:none;flex-direction:column;position:absolute;top:0;right:100%;" +
            "margin-right:2px;min-width:90px;background:#fff;border-radius:4px;" +
            "box-shadow:0 1px 4px rgba(0,0,0,0.3);overflow:hidden;z-index:2;";
    }

    /** Tints the button when inspect is on (any non-off mode). */
    private applyActiveStyle(): void {
        if (!this.button) return;
        const on = this.mode !== "off";
        this.button.style.backgroundColor = on ? "#ec7211" : "";
        this.button.style.color = on ? "#fff" : "";
    }
}
